import { API_BASE, ApiError, getToken, privateDocumentBlob, req } from "./transport";
import { profileFetch } from "../profileSession";

export type SupportTicket = {
  id: string; subject: string; category: string; status: string; outcome?: string | null;
  createdAt: string; lastReplyAt?: string; lastSender?: string | null; userName?: string | null;
};
export type SupportImage = { id: string; position: number; contentType: string };
export type SupportMessage = { id: string; byRole: string; body: string; createdAt: string; images?: SupportImage[] };
export type SupportConversation = { ticket: SupportTicket; messages: SupportMessage[] };
export type SupportRequest = { subject: string; category: string; body: string; requestId: string; images?: File[] };
const authorized = { rejectUnauthorized: true };
async function sendSupport<T>(path: string, fields: Record<string, string>, images: File[] = []): Promise<T> {
  if (!images.length) return req<T>(path, { method: "POST", body: JSON.stringify(fields) }, authorized);
  const form = new FormData();
  for (const [name, value] of Object.entries(fields)) form.append(name, value);
  for (const image of images) form.append("images", image);
  const token = getToken();
  const response = await profileFetch(`${API_BASE}${path}`, { method: "POST", body: form, credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!response.ok) {
    const failure: { error?: string; code?: string } = await response.json().catch(() => ({}));
    throw new ApiError(response.status, failure.error || "Could not confirm this submission. Retry.", failure.code);
  }
  return response.json();
}
export const supportApi = {
  createTicket(input: SupportRequest) {
    const { images, ...fields } = input;
    return sendSupport<{ ok: boolean; ticket: SupportTicket }>("/support", fields, images);
  },
  async myTickets() {
    return (await req<{ tickets: SupportTicket[] }>("/support", {}, authorized)).tickets;
  },
  ticket(id: string) { return req<SupportConversation>(`/support/${encodeURIComponent(id)}`, {}, authorized); },
  replyTicket(id: string, body: string, requestId: string, images: File[] = []) {
    return sendSupport<{ ok: boolean; ticket: SupportTicket; messages: SupportMessage[] }>(`/support/${encodeURIComponent(id)}/reply`, { body, requestId }, images);
  },
  async adminTickets(status = "unresolved") {
    return (await req<{ tickets: SupportTicket[] }>(`/admin/support?status=${encodeURIComponent(status)}`, {}, authorized)).tickets;
  },
  adminTicket(id: string) { return req<SupportConversation>(`/admin/support/${encodeURIComponent(id)}`, {}, authorized); },
  adminReplyTicket(id: string, body: string, requestId: string, images: File[] = []) {
    return sendSupport<{ ok: boolean; ticket: SupportTicket; messages: SupportMessage[] }>(`/admin/support/${encodeURIComponent(id)}/reply`, { body, requestId }, images);
  },
  supportImage(id: string, imageId: string, admin: boolean, signal: AbortSignal) {
    return privateDocumentBlob(`${admin ? "/admin" : ""}/support/${encodeURIComponent(id)}/images/${encodeURIComponent(imageId)}`, signal);
  },
  adminSetTicketStatus(id: string, status: "open" | "in_progress" | "resolved", outcome?: string, priority?: "low" | "normal" | "high", reviewedMemberMessageCount?: number) {
    return req<{ ok: boolean; status: string; ticket: SupportTicket }>(`/admin/support/${encodeURIComponent(id)}/status`, { method: "POST", body: JSON.stringify({ status, outcome, priority, reviewedMemberMessageCount }) }, authorized);
  }
};
